import classNames from 'classnames';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import AppPaths from '../../../../AppPaths';
import { BillingSubscription, SubscriptionOption } from '../SplashPage';
import { centsToDollars, centsToDollarsNumber, expectUnreachable } from '../../../../lib/util';
import pillStyles from '../styles/SupplementPill.module.scss';
import purchasablePillStyles from '../../../Checkout/components/Supplements/ProductSelector/ProductSelector.module.scss';
import { supplementsManagement as events } from '../../../../lib/analytics/events';
import { WeightRange } from '../../../../types/gql-op-types';
import { ReactComponent as DownArrow } from '../../../../assets/images/icons/arrow_down.svg';
import { SupplementCheckoutFlow } from '../../../../types';
import { ReactComponent as ArrowIcon } from '../../../../assets/images/icons/supplements_management/icon_arrow.svg';
import { ReactComponent as CalendarIcon } from '../../../../assets/images/icons/supplements_management/icon_calendar.svg';
import { ReactComponent as TruckIcon } from '../../../../assets/images/icons/supplements_management/icon_truck.svg';

export function getRecommendedText(recommendedForDogWeightRangePounds: WeightRange, t?: any) {
  const { min, max } = recommendedForDogWeightRangePounds ?? { min: null, max: null };
  if (min && max) {
    return t ? t('recommended_for_dogs_min_max_lbs', { min, max }) : `Recommended for dogs ${min}-${max}lbs`;
  } else if (!min) {
    return t ? t('recommended_for_dogs_up_to_max_lbs', { max }) : `Recommended for dogs up to ${max}lbs`;
  } else if (!max) {
    return t ? t('recommended_for_dogs_over_min_lbs', { min }) : `Recommended for dogs over ${min}lbs`;
  }
  return '';
}

interface InformationSupplementPillProps {
  hasMultipleSubscriptions: boolean;
  subscription: BillingSubscription;
  pendingPlan?: SubscriptionOption;
}

function InformationSupplementPill({
  hasMultipleSubscriptions,
  subscription,
  pendingPlan,
}: InformationSupplementPillProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { supplementShipmentDates, nextSupplementShipmentDetails } = subscription;
  if (!supplementShipmentDates) {
    throw new Error('Missing shipment dates. Please contact support@tryfi.com.');
  }
  const { weeksBetweenShipments } = subscription.subscriptionOption;

  // TODO(aaronmorais): Localize shipment statuses
  const shipmentStatus = nextSupplementShipmentDetails?.status;
  const expectedDeliveryDate = nextSupplementShipmentDetails?.deliveryExpected;
  const trackingLink = nextSupplementShipmentDetails?.trackingLink;

  const handleManageClick = () => {
    events.manageSubscription({ subscriptionId: subscription.id });
    history.push({
      pathname: AppPaths.Supplements.Manage(subscription.id),
      state: { hasMultipleSubscriptions, subscription, pendingSubscription: pendingPlan },
      search: window.location.search,
    });
  };

  const shippingCadenceText = () => {
    // If there are pending changes (meaning the user has updated this subscription previously), we need to get more
    // information about the future plan so we can display it to the user. Behind the scenes, in Recurly, their plan will only be updated
    // on their next bill date (to avoid shipping a new bag of supplements prematurely), but we make an immediate change to the next bill
    // date, which appears in the UI, so it should appear as though the plan change has also happened immediately. To do this,
    // we'll get the plan information of the new plan and display it here.
    let shippingCadence = weeksBetweenShipments;
    if (pendingPlan) {
      shippingCadence = pendingPlan.weeksBetweenShipments;
    }

    return t('ships_every_weeks_weeks', { weeks: shippingCadence });
  };

  const nextShipmentText = () => {
    return `${DateTime.fromISO(supplementShipmentDates.displayDate).toFormat('cccc, LLL d')}`;
  };

  const expectedDeliveryText = () => {
    if (shipmentStatus === 'Shipment pending') {
      return t('tracking_available_once_shipped');
    }
    if (!expectedDeliveryDate) {
      return t('make_changes_by_date', {
        date: DateTime.fromISO(supplementShipmentDates.displayDate).minus({ minutes: 1 }).toFormat('cccc, LLL d'),
      });
    }
    return t('delivery_expected_date', { date: DateTime.fromISO(expectedDeliveryDate).toFormat('L/d') });
  };

  return (
    <div
      className={classNames(pillStyles.supplementPillContainer, pillStyles.informational)}
      onClick={handleManageClick}
    >
      <div className={pillStyles.row}>
        <div className={pillStyles.informationalIcon}>
          {shipmentStatus === 'Scheduled' ? <CalendarIcon /> : <TruckIcon />}
        </div>
        <div className={pillStyles.col}>
          <p className={pillStyles.status}>{shipmentStatus === 'Scheduled' ? t('next_shipment') : shipmentStatus}</p>
          <h2>{nextShipmentText()}</h2>
          {expectedDeliveryText && (
            <p className={pillStyles.deliveryExpected}>
              {expectedDeliveryText()}
              {trackingLink && (
                <span>
                  &nbsp; {'·'} &nbsp;
                  <a className={pillStyles.trackingLink} href={trackingLink} target="_blank" rel="noreferrer">
                    {t('track')}
                  </a>
                </span>
              )}
            </p>
          )}
          <div className={pillStyles.shipping}>{shippingCadenceText()}</div>
        </div>
        <ArrowIcon />
      </div>
    </div>
  );
}

interface SupplementPillUpdateOptionProps {
  subscriptionOption: SubscriptionOption;
  handleClick: () => void;
  selected: boolean;
}
export function SupplementPillUpdateOption({
  subscriptionOption,
  handleClick,
  selected,
}: SupplementPillUpdateOptionProps) {
  const { t } = useTranslation();
  const { sku, recommendedForDogWeightRangePounds, weeksBetweenShipments } = subscriptionOption;
  if (!sku) {
    throw new Error(t('no_sku_found'));
  }

  return (
    <>
      <div
        className={classNames(pillStyles.supplementPillContainer, pillStyles.selectable, {
          [pillStyles.selected]: selected,
        })}
        onClick={handleClick}
      >
        <div className={pillStyles.shipping}>{t('ships_every_weeks_weeks', { weeks: weeksBetweenShipments })}</div>
        {recommendedForDogWeightRangePounds && (
          <div className={pillStyles.recommended}>{getRecommendedText(recommendedForDogWeightRangePounds, t)}</div>
        )}
      </div>
    </>
  );
}

interface SupplementPillPurchaseOptionProps {
  subscriptionOption: SubscriptionOption;
  handleClick?: () => void;
  selected: boolean;
  showDownArrow?: boolean;
  flow?: SupplementCheckoutFlow;
}
export function SupplementPillPurchaseOption({
  subscriptionOption,
  handleClick,
  selected,
  showDownArrow = false,
  flow = 'ecomm',
}: SupplementPillPurchaseOptionProps) {
  const { t } = useTranslation();
  return (
    <div
      className={classNames(pillStyles.purchasableOption, purchasablePillStyles.supplementProductSelector, {
        [pillStyles.selected]: selected,
      })}
      onClick={handleClick}
    >
      <div className={purchasablePillStyles.top}>
        <div className={purchasablePillStyles.left}>
          <div className={purchasablePillStyles.size}>{subscriptionOption.name}</div>
          <div className={purchasablePillStyles.shippingCadence}>
            {t('ships_every_weeks_rate_units', {
              weeks: subscriptionOption.weeksBetweenShipments,
              unit: subscriptionOption.displayedRateUnit,
            })}
          </div>
          {subscriptionOption.recommendedForDogWeightRangePounds && (
            <div className={purchasablePillStyles.recommended}>
              {' '}
              {getRecommendedText(subscriptionOption.recommendedForDogWeightRangePounds, t)}
            </div>
          )}
        </div>
        <div className={purchasablePillStyles.right}>
          <div className={purchasablePillStyles.price}>
            <strong>
              {subscriptionOption.displayedRate}
              {'/'}
              {subscriptionOption.displayedRateUnit}
            </strong>
          </div>
          <DownArrow className={classNames({ [pillStyles.hideDown]: !showDownArrow })} />
        </div>
      </div>
      <div className={purchasablePillStyles.bottom}>
        <div className={purchasablePillStyles.divider} />
        {flow === 'ecomm' ? (
          <span className={purchasablePillStyles.billingCadence}>
            {t('billed_as_amount_per_shipment', { amount: centsToDollarsNumber(subscriptionOption.priceInCents) })}
          </span>
        ) : (
          <span className={purchasablePillStyles.billingCadence}>
            <Trans
              i18nKey="billed_as_amount_original_per_shipment"
              values={{
                amount: centsToDollars(subscriptionOption.priceInCents),
                original: centsToDollars(subscriptionOption.discountedFromOriginalPriceInCents!),
              }}
              components={{
                originalPrice: <span className={purchasablePillStyles.originalPrice} />,
              }}
            />
          </span>
        )}
      </div>
    </div>
  );
}

type SupplementPillVersion =
  | 'informational' // Display information about a user's existing plan.
  | 'selectable'; // Display a selectable subscription pill for user to select when changing plan.

interface CommonSupplementPillProps {
  version: SupplementPillVersion;
  paused?: boolean;
  handleClick?: () => void;
  selected?: boolean;
  pendingPlan?: SubscriptionOption;
  hasMultipleSubscriptions?: boolean;
}

interface InfoPillProps {
  subscription: BillingSubscription;
  subscriptionDetails: null;
}

interface UpdatePillProps {
  subscription: BillingSubscription;
  subscriptionDetails: SubscriptionOption;
}

type ConditionalSupplementPillProps = InfoPillProps | UpdatePillProps;

export default function SupplementPill({
  hasMultipleSubscriptions = false,
  subscription,
  subscriptionDetails,
  version,
  handleClick,
  selected = false,
  pendingPlan,
}: CommonSupplementPillProps & ConditionalSupplementPillProps) {
  const { t } = useTranslation();
  const subscriptionOption = subscriptionDetails ? subscriptionDetails : subscription.subscriptionOption;
  switch (version) {
    case 'informational':
      return (
        subscription && (
          <InformationSupplementPill
            hasMultipleSubscriptions={hasMultipleSubscriptions}
            subscription={subscription}
            pendingPlan={pendingPlan}
          />
        )
      );
    case 'selectable':
      return (
        subscription && (
          <SupplementPillUpdateOption
            subscriptionOption={subscriptionOption}
            selected={selected}
            handleClick={() => handleClick && handleClick()}
          />
        )
      );

    default:
      expectUnreachable(version);
      throw new Error(t('unknown_supplement_pill_version_version', { version }));
  }
}
